import React, { useState, useEffect, useCallback } from 'react';
import { ArrowLeft, Loader2, AlertTriangle, BookOpen } from 'lucide-react';
import { COR, COR_ESCURA, COR_CLARA } from '../BlocosManual';

// ============================================================================
// BIBLIOTECA › CONECTA — CURADORIA do Manual do Portal (DEM-00022, tarefa 14)
// ============================================================================
// O "Manual do Portal" é um manual da aba Manuais montado a partir de comunicados
// do Conecta marcados aqui como "ligados ao Portal" e classificados por MÓDULO
// (cada módulo = 1 capítulo). Esta tela é a curadoria: percorre as edições
// publicadas e, para cada comunicado, escolhe o módulo (ou "não incluído").
//
// ⚠️ SÓ curadores (Marketing/Admin/Diretoria) — o backend é a trava real
//    (rotas `authorizeRoles(PERFIS_CURA)`); aqui o botão de entrada já é gated.
//
// ⚠️ A curadoria é uma CAMADA À PARTE da edição: mora em `dtbConectaPortalItem`,
//    não no CONTEUDO_JSON — assim o republish da edição (Marketing) não apaga o
//    que foi curado. A referência ao comunicado é (CHAVE + uid).
// ============================================================================

const FONTE = "'Raleway', sans-serif";
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('authToken')}` });

// Valor do select para "não faz parte do manual".
const NAO_INCLUIDO = '';

const CuradoriaPortal = ({ isMobile, onSair }) => {
  // { estado: 'carregando'|'erro'|'pronto', edicoes: [], modulos: [], orfaos: [] }
  const [dados, setDados] = useState({ estado: 'carregando', edicoes: [], modulos: [], orfaos: [] });
  const [salvando, setSalvando] = useState(false);

  const carregar = useCallback(async () => {
    setDados((d) => ({ ...d, estado: 'carregando' }));
    try {
      const [rInv, rMod, rCur] = await Promise.all([
        fetch('/api/biblioteca/conecta/portal/comunicados', { headers: auth() }),
        fetch('/api/biblioteca/conecta/portal/modulos', { headers: auth() }),
        fetch('/api/biblioteca/conecta/portal/curadoria', { headers: auth() }),
      ]);
      if (!rInv.ok || !rMod.ok || !rCur.ok) throw new Error('http');
      const inv = await rInv.json();
      const modulos = await rMod.json();
      const curadoria = await rCur.json();
      // Órfãs: curadorias cujo comunicado sumiu numa reedição (o inventário não as
      // mostra, porque parte dos comunicados vivos). Aqui damos como limpá-las.
      const orfaos = curadoria.filter((c) => c.orfao);
      setDados({ estado: 'pronto', edicoes: inv.edicoes || [], modulos, orfaos });
    } catch {
      setDados({ estado: 'erro', edicoes: [], modulos: [], orfaos: [] });
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  // Escolher um módulo cura (ou muda o módulo); escolher "não incluído" descura.
  const mudar = async (chave, comunicado, modulo) => {
    setSalvando(true);
    try {
      if (modulo === NAO_INCLUIDO) {
        if (comunicado.curado) {
          await fetch(`/api/biblioteca/conecta/portal/curadoria/${comunicado.curado.id}`, {
            method: 'DELETE', headers: auth(),
          });
        }
      } else {
        await fetch('/api/biblioteca/conecta/portal/curadoria', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...auth() },
          body: JSON.stringify({ chave, comunicadoUid: comunicado.uid, modulo }),
        });
      }
      await carregar();
    } catch { /* silencioso — recarrega mantém a verdade do banco */ }
    finally { setSalvando(false); }
  };

  const limparOrfa = async (id) => {
    setSalvando(true);
    try {
      await fetch(`/api/biblioteca/conecta/portal/curadoria/${id}`, { method: 'DELETE', headers: auth() });
      await carregar();
    } catch { /* silencioso */ }
    finally { setSalvando(false); }
  };

  return (
    <div>
      {/* topo */}
      <button
        type="button"
        onClick={onSair}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 16,
          padding: '6px 12px', borderRadius: 8, border: `1px solid ${COR}44`,
          background: COR_CLARA, color: COR_ESCURA, fontFamily: FONTE, fontSize: 13,
          fontWeight: 700, cursor: 'pointer',
        }}
      >
        <ArrowLeft size={14} /> Voltar às edições
      </button>

      <div style={{
        display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8,
      }}>
        <div style={{
          width: 44, height: 44, borderRadius: 12, background: COR_CLARA,
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <BookOpen size={22} style={{ color: COR }} />
        </div>
        <div>
          <h2 style={{ margin: 0, fontFamily: FONTE, fontSize: isMobile ? 20 : 24, fontWeight: 800, color: '#1c1917' }}>
            Curar o Manual do Portal
          </h2>
          <p style={{ margin: '2px 0 0', fontSize: 13.5, color: '#78716c', lineHeight: 1.5 }}>
            Escolha o módulo de cada comunicado que deve entrar no manual. Cada módulo vira um capítulo.
          </p>
        </div>
        {salvando && <Loader2 size={16} className="animate-spin" style={{ color: COR, marginLeft: 'auto' }} />}
      </div>

      {dados.estado === 'carregando' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#78716c', padding: 24 }}>
          <Loader2 size={18} className="animate-spin" /> Carregando comunicados…
        </div>
      )}

      {dados.estado === 'erro' && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10, padding: 16, borderRadius: 12,
          background: '#fef2f2', border: '1px solid #fca5a5', color: '#b91c1c',
        }}>
          <AlertTriangle size={18} /> Não foi possível carregar a curadoria.
          <button type="button" onClick={carregar} style={{ marginLeft: 'auto', border: 'none', background: 'transparent', color: '#b91c1c', fontWeight: 800, cursor: 'pointer' }}>
            Tentar de novo
          </button>
        </div>
      )}

      {dados.estado === 'pronto' && dados.edicoes.length === 0 && (
        <p style={{
          padding: '14px 18px', borderRadius: 12, background: '#fafaf9',
          border: '1px dashed #d6d3d1', fontSize: 13.5, lineHeight: 1.6, color: '#78716c',
        }}>
          Nenhuma edição publicada ainda. Publique uma edição no Gerador do Conecta (Marketing) para curar seus comunicados.
        </p>
      )}

      {/* órfãs — curadoria cujo comunicado sumiu numa reedição */}
      {dados.estado === 'pronto' && dados.orfaos.length > 0 && (
        <div style={{
          marginBottom: 24, padding: '14px 16px', borderRadius: 12,
          background: '#fffbeb', border: '1px solid #fcd34d',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#92400e', fontWeight: 800, fontSize: 13.5, marginBottom: 8 }}>
            <AlertTriangle size={16} /> Comunicados removidos ({dados.orfaos.length})
          </div>
          <p style={{ margin: '0 0 10px', fontSize: 12.5, color: '#78716c', lineHeight: 1.5 }}>
            Estes comunicados foram curados mas não existem mais na edição (removidos numa reedição). Ficam de fora do manual — pode limpar.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {dados.orfaos.map((o) => (
              <div key={o.id} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13 }}>
                <span style={{ color: '#57534e' }}>{o.edicaoTitulo} · módulo {o.modulo}</span>
                <button type="button" onClick={() => limparOrfa(o.id)} style={{
                  marginLeft: 'auto', padding: '3px 10px', borderRadius: 6, border: '1px solid #fca5a5',
                  background: '#fff', color: '#b91c1c', fontFamily: FONTE, fontSize: 12, fontWeight: 700, cursor: 'pointer',
                }}>
                  Limpar
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* edições → comunicados */}
      {dados.estado === 'pronto' && dados.edicoes.map((ed) => (
        <section key={ed.chave} style={{ marginBottom: 24 }}>
          <h3 style={{
            margin: '0 0 10px', fontFamily: FONTE, fontSize: 13, fontWeight: 800,
            color: '#a8a29e', textTransform: 'uppercase', letterSpacing: 0.7,
          }}>
            {ed.titulo}
          </h3>
          {ed.comunicados.length === 0 && (
            <p style={{ margin: 0, fontSize: 13, color: '#a8a29e' }}>Esta edição não tem comunicados.</p>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {ed.comunicados.map((com) => {
              const moduloAtual = com.curado ? com.curado.modulo : NAO_INCLUIDO;
              return (
                <div key={com.uid || com.indice} style={{
                  display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
                  padding: '10px 14px', borderRadius: 10, background: '#fff',
                  border: `1px solid ${com.curado ? COR + '55' : '#e7e5e4'}`,
                }}>
                  <span style={{ flex: 1, minWidth: 180, fontSize: 14, color: '#292524', fontWeight: 600 }}>
                    {com.assunto}
                  </span>
                  <select
                    value={moduloAtual}
                    disabled={!com.uid /* comunicado sem uid não é referenciável */}
                    onChange={(e) => mudar(ed.chave, com, e.target.value)}
                    style={{
                      padding: '6px 10px', borderRadius: 8, border: '1px solid #e7e5e4',
                      fontFamily: FONTE, fontSize: 13, color: '#292524', background: '#fafaf9',
                      minWidth: 200, cursor: 'pointer',
                    }}
                  >
                    <option value={NAO_INCLUIDO}>— não incluído no manual —</option>
                    {dados.modulos.map((m) => (
                      <option key={m} value={m}>{m}</option>
                    ))}
                  </select>
                </div>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
};

export default CuradoriaPortal;
